import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

const initialState = {
  products: {
    all_collection: [],
    all_product: {},
  },
  totalProducts: 0,
  isOnlyOneDiscountProduct: false,
  brands: [],
  tags: [],
  minPrice: 0,
  maxPrice: -1,
  maxPriceLimit: 1000,
  maxDiscount: 0,
  selectedDiscountAbove: 0,
  selectedBrands: [],
  selectedTags: [],
  lastCategoryIds: '',
  loading: false,
  error: null,
};

const fetchBrandsAndTagsFromProduct = (products, all_collection) => {
  try {
    if (Object.keys(products).length === 0 || !products || Object.keys(all_collection).length === 0 || !all_collection) {
      return { brands: [], tags: [], maxPrice: initialState?.maxPriceLimit || 1000, maxDiscount: 0, totalProducts: 0 };
    }
  
    const selectedCategoryIDs = Object.values(all_collection).map(category => category.collection_id);
    // const flat_products = Object.values(products).flat(); // all products in all categories
    const flat_products = selectedCategoryIDs.map(categoryId => products[categoryId] || []).flat(); // all products in selected categories
    const brands = flat_products.map(product => product.brand?.trim().toString()).filter(brand => brand !== '' && brand !== null && brand !== undefined);
    const tags = flat_products.map(product => product.tags?.trim().split(',') || []).flat().filter(tags => tags !== '' && tags !== null && tags !== undefined);
    const maxPrice = Math.max(...flat_products.map(product => product.price)) || initialState?.maxPriceLimit;
    const maxDiscount = Math.max(...flat_products.map(product => product.compare_price > 0 ? ((product.compare_price - product.price) / product.compare_price) * 100 : 0)) || 0;
    const isOnlyOneDiscountProduct = flat_products.filter(product => product.compare_price > 0).length === 1;

    return { brands, tags, maxPrice, maxDiscount, totalProducts: flat_products.length, isOnlyOneDiscountProduct };
  } catch {
    return { brands: [], tags: [], maxPrice: initialState?.maxPriceLimit || 1000, maxDiscount: 0, totalProducts: 0 };
  }
}

// Async thunk for setting products with loading states
export const setProducts = createAsyncThunk(
  'products/setProducts',
  async (productsData, { rejectWithValue }) => {
    try {
      // Add a small delay to ensure pending state is visible in UI
      await new Promise(resolve => setTimeout(resolve, 0));
      
      // Process products data
      const { brands, tags, maxPrice, maxDiscount, totalProducts, isOnlyOneDiscountProduct } = fetchBrandsAndTagsFromProduct(productsData.all_product, productsData.all_collection);
      return {
        products: productsData,
        brands: [...new Set(brands)],
        tags: [...new Set(tags)],
        maxPriceLimit: maxPrice,
        maxDiscount: maxDiscount,
        totalProducts: totalProducts,
        isOnlyOneDiscountProduct: isOnlyOneDiscountProduct,
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to set products');
    }
  }
);

const productsSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    updateProducts: (state, action) => {      
      // Merge all_collection arrays, avoiding duplicates based on collection_id
      const existingCollectionIds = new Set(
        (state.products.all_collection || []).map(c => c.collection_id)
      );
      const newCollections = (action.payload.all_collection || []).filter(
        c => !existingCollectionIds.has(c.collection_id)
      );
      const mergedCollections = [
        ...(state.products.all_collection || []),
        ...newCollections,
      ];

      // Merge all_product objects, appending products within each category
      const mergedProducts = { ...(state.products.all_product || {}) };
      
      if (action.payload.all_product) {
        Object.keys(action.payload.all_product).forEach(categoryId => {
          const existingProducts = mergedProducts[categoryId] || [];
          const newProducts = action.payload.all_product[categoryId] || [];
          
          // Create a Set of existing product IDs to avoid duplicates
          const existingProductIds = new Set(
            existingProducts.map(p => p.product_id || p.id)
          );
          
          // Filter out products that already exist
          const uniqueNewProducts = newProducts.filter(
            p => !existingProductIds.has(p.product_id || p.id)
          );
          
          // Append unique new products
          mergedProducts[categoryId] = [...existingProducts, ...uniqueNewProducts];
        });
      }

      state.products = {
        all_collection: mergedCollections,
        all_product: mergedProducts,
      };

      // Recalculate brands, tags, and maxPrice from the merged data
      const { brands, tags, maxPrice } = fetchBrandsAndTagsFromProduct(
        mergedProducts,
        mergedCollections
      );
      state.brands = [...new Set([...state.brands, ...brands])];
      state.tags = [...new Set([...state.tags, ...tags])];
      state.maxPriceLimit = Math.max(state.maxPriceLimit, maxPrice);
    },
    clearProducts: (state) => {
      state.products = initialState.products;
    },
    setSelectedBrands: (state, action) => {
      state.selectedBrands = action.payload;
    },
    setSelectedTags: (state, action) => {
      state.selectedTags = action.payload;
    },
    clearSelectedBrands: (state) => {
      state.selectedBrands = [];
    },
    clearSelectedTags: (state) => {
      state.selectedTags = [];
    },
    setMinPrice: (state, action) => {
      if (action.payload < 0 || action.payload === state.maxPrice) {
        return;
      }
      state.minPrice = action.payload;
    },
    setMaxPrice: (state, action) => {
      if (action.payload < 0 || action.payload === state.minPrice) {
        return;
      }
      state.maxPrice = action.payload;
    },
    setSelectedDiscountAbove: (state, action) => {
      state.selectedDiscountAbove = action.payload;
    },
    clearSelectedDiscountAbove: (state) => {
      state.selectedDiscountAbove = 0;
    },
    clearMinMaxPrice: (state) => {
      state.minPrice = 0;
      state.maxPrice = state.maxPriceLimit;
    },
    clearMaxPriceLimit: (state) => {
      state.maxPriceLimit = initialState?.maxPriceLimit || 1000;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle setProducts pending state
      .addCase(setProducts.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        // Clear brands and tags when starting to load new products
        state.brands = [];
        state.tags = [];
        state.maxPriceLimit = initialState?.maxPriceLimit || 1000;
        state.maxDiscount = 0;
        state.totalProducts = 0;
        state.isOnlyOneDiscountProduct = false;

        // Check if category IDs are the same
        const newCategoryIds = action.meta.arg?.all_collection
          ?.map((c) => c.collection_id)
          .sort()
          .join(',');

        if (newCategoryIds && newCategoryIds.length > 0 && action.meta.arg?.all_collection?.length === 1) {
          if (state.lastCategoryIds !== newCategoryIds) {
            state.minPrice = 0;
            state.maxPrice = state.maxPriceLimit;
            state.selectedBrands = [];
            state.selectedTags = [];
            state.lastCategoryIds = newCategoryIds;
            state.selectedDiscountAbove = 0;
          }
        }
      })
      // Handle setProducts fulfilled state
      .addCase(setProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.products = action.payload.products;
        state.brands = action.payload.brands;
        state.tags = action.payload.tags;
        state.maxDiscount = action.payload.maxDiscount;
        state.totalProducts = action.payload.totalProducts;
        state.isOnlyOneDiscountProduct = action.payload.isOnlyOneDiscountProduct;
        
        if (state.maxPrice === state.maxPriceLimit) {
          state.maxPrice = action.payload.maxPriceLimit;
        }

        state.maxPriceLimit = action.payload.maxPriceLimit;
      })
      // Handle setProducts rejected state
      .addCase(setProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to set products';
      });
  },
});

export const { updateProducts, clearProducts, setSelectedBrands, setSelectedTags, clearSelectedBrands, clearSelectedTags, setMinPrice, setMaxPrice, clearMinMaxPrice, clearMaxPriceLimit, setSelectedDiscountAbove, clearSelectedDiscountAbove } =
  productsSlice.actions;
export default productsSlice.reducer;
